import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation,
  RESPONSE_INIT,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { TipsService } from '@services/http/tips.service';
import { setBreadCrumb } from '@utils/setBreadCrumb';
import { Tip } from '@models/guides/tips.model';

import { DomSanitizer } from '@angular/platform-browser';

import {
  isPlatformBrowser,
  NgClass,
  NgOptimizedImage,
  TitleCasePipe,
} from '@angular/common';
import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';
// Component
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
// Pipe
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SEOService } from '@services/logic/seo.service';
import { SafePipe } from '@app/pipes/safe';
import { HappyHelperBannerComponent } from '@components/seoPageElements/happy-helper-banner/happy-helper-banner.component';
import { HappyHelperUspsComponent } from '@components/seoPageElements/happy-helper-usps/happy-helper-usps.component';
import { ServerSideResponseService } from '@services/logic/serverSideResponse.service';

@Component({
  imports: [
    CommonModule,
    TitleCasePipe,
    ScrollProgressBarDirective,
    BreadcrumbListComponent,
    FooterComponent,
    JsonLdPipe,
    NgOptimizedImage,
    NgClass,
    SafePipe,
    HappyHelperBannerComponent,
    HappyHelperUspsComponent,
  ],
  providers: [SEOService],
  encapsulation: ViewEncapsulation.None,
  selector: 'app-tips-details',
  standalone: true,
  templateUrl: './tips-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tips-details.component.scss'],
})
export class TipsDetailsComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly cdr = inject(ChangeDetectorRef);

  private destroy$: Subject<void> = new Subject<void>();
  partsOfUrl!: { name: string; url: string }[];
  tip!: Tip;
  mainCat!: string;
  subCat?: string;
  slug!: string;
  tasks: any = [];
  content?: string;
  url?: string[];
  dangerousVideoUrl?: string;
  youTubeVidLink: any;
  videoLoaded: boolean = false;
  youtubeSchema: any;
  howToSchema: any;
  topRelatedTips: any[] = [];
  private readonly isBrowser: boolean;
  private readonly cleaningCategories = ['rengoering', 'rengøring'];
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private tipsService: TipsService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private seoService: SEOService,
    private serverSideResponseService: ServerSideResponseService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.fetchTip();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateVideoUrl(id: string) {
    if (id && id !== '') {
      this.dangerousVideoUrl = `https://www.youtube.com/embed/${id}`;
      this.youTubeVidLink = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.dangerousVideoUrl,
      );
      this.videoLoaded = true;
    } else {
      this.videoLoaded = false;
    }
  }

  private fetchTip(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.mainCat = this.route.snapshot.paramMap.get('headCategory')!;
      this.subCat = this.route.snapshot.paramMap.get('subCategory') ?? '';
      this.slug = this.route.snapshot.paramMap.get('guideSlug') ?? '';
      this.url = this.router.url.split('/').filter((subUrl) => subUrl);
      this.tipsService
        .fetchTip(this.slug, this.mainCat, this.subCat)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res: Tip) => {
            if (!res && this.responseInit) {
              this.responseInit.status = 404;
              return;
            }
            this.tip = res;
            this.tip.content.rich_text = this.tip.content.rich_text.replace(
              /\\n/g,
              '\n',
            );
            /*
             if (this.env.production && this.tip?.title) {
              this.fetchSimiliarTasks();
            }*/
            this.updateVideoUrl(res.content.youtube_embed_id);

            if (
              res.content.youtube_schema &&
              res.content.youtube_schema !== ''
            ) {
              this.injectYoutubeSchema(res.content.youtube_schema);
            }
            if (res.how_to_schema && res.content.how_to_schema !== '') {
              this.injectHowToSchema(res.how_to_schema);
            }
            this.setBreadCrumb();
            this.setMetaDetails();
            this.cdr.markForCheck();
          },
          (err) => {
            // 301 Permanent redirects handle correct by response status code!
            if (err.status === 200 && err.url && this.isBrowser) {
              // Refresh the component with the new URL
              window.location.href = err.url;
            }
            if (err.status === 301) {
              this.router.navigate([err.error.url]);
            }
            if (err.status === 404) {
              // Set 404 status code for server response during SSR
              this.serverSideResponseService.setNotFound();
              this.router.navigate(['/404'], { skipLocationChange: true });
            }
          },
        );
    });
  }

  setBreadCrumb() {
    this.partsOfUrl = setBreadCrumb({
      entity: this.tip,
      hubIndex: this.seg.suffix.hubIndex,
      url: this.router.url,
    });
  }

  injectYoutubeSchema(schema: any) {
    this.youtubeSchema = schema;
  }

  injectHowToSchema(schema: string | object) {
    this.howToSchema = schema;
  }

  setMetaDetails() {
    this.seoService.setPageMeta({
      metaTitle: `${this.tip.titleTag}`,
      metaDesc: this.tip.descTag!,
      canonicalUrl: `${this.env.domain}${this.router.url}`,
    });
  }

  isCleaningCategory(): boolean {
    return this.cleaningCategories.includes(
      this.tip?.catSlug?.toLowerCase() || '',
    );
  }
}
